import { useEffect, useId, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './ui';

const url = process.env.REACT_APP_URL;

function FooterLink({ link }) {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer">
        {link.label}
      </a>
    );
  }

  if (link.href.startsWith('/#') || link.href.startsWith('#')) {
    const to = link.href.startsWith('/') ? link.href : `/${link.href}`;
    return <Link to={to}>{link.label}</Link>;
  }

  return <Link to={link.href}>{link.label}</Link>;
}

function NavLink({ link, isHome, onNavigate }) {
  if (link.href.startsWith('/')) {
    return (
      <Link to={link.href} onClick={onNavigate}>
        {link.label}
      </Link>
    );
  }

  if (isHome) {
    return (
      <a href={link.href} onClick={onNavigate}>
        {link.label}
      </a>
    );
  }

  return (
    <Link to={`/${link.href}`} onClick={onNavigate}>
      {link.label}
    </Link>
  );
}

function HeaderSearch({ t, onSearch }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlQuery = location.pathname === '/buscar' ? params.get('q') || '' : '';
  const [query, setQuery] = useState(urlQuery);

  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  function handleSubmit(event) {
    event.preventDefault();
    const q = query.trim();
    navigate(q ? `/buscar?q=${encodeURIComponent(q)}` : '/buscar');
    onSearch?.();
  }

  return (
    <form className="header-search" role="search" onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor="header-advisor-search">
        {t.search.ariaLabel}
      </label>
      <input
        id="header-advisor-search"
        type="search"
        name="q"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t.search.placeholder}
        autoComplete="off"
        enterKeyHint="search"
      />
      <button type="submit" className="header-search__btn" aria-label={t.search.submit}>
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path
            fill="currentColor"
            d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
          />
        </svg>
      </button>
    </form>
  );
}

export default function SiteLayout({ t, children }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash, location.search]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <div className={`app${menuOpen ? ' app--menu-open' : ''}`}>
      <a href="#contenido" className="skip-link">
        {t.skipLink}
      </a>

      <header className="site-header">
        <div className="shell site-header__inner">
          <Logo homeLabel={t.brandHome} />
          <HeaderSearch t={t} onSearch={closeMenu} />
          <button
            type="button"
            className={`menu-toggle${menuOpen ? ' menu-toggle--open' : ''}`}
            aria-label={menuOpen ? t.closeMenu : t.openMenu}
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
          <div
            id={menuId}
            className={`site-header__panel${menuOpen ? ' is-open' : ''}`}
          >
            <nav className="site-nav" aria-label={t.navAria}>
              <ul>
                {t.nav.map((link) => (
                  <li key={link.href}>
                    <NavLink link={link} isHome={isHome} onNavigate={closeMenu} />
                  </li>
                ))}
              </ul>
            </nav>
            <div className="header-actions">
              <a
                href={`${url}/login`}
                target="_blank"
                rel="noopener noreferrer"
                className="header-link"
                onClick={closeMenu}
              >
                {t.header.login}
              </a>
              <a
                href={`${url}/register`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                onClick={closeMenu}
              >
                {t.header.schedule}
              </a>
            </div>
          </div>
        </div>
        {menuOpen ? (
          <button
            type="button"
            className="menu-backdrop"
            aria-label={t.closeMenu}
            onClick={closeMenu}
          />
        ) : null}
      </header>

      {children}

      <footer className="site-footer">
        <div className="shell site-footer__top">
          <Logo homeLabel={t.brandHome} />
        </div>
        <div className="shell site-footer__grid">
          {t.footer.columns.map((column) => (
            <div key={column.title} className="footer-column">
              <h3>{column.title}</h3>
              <ul>
                {column.links.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="shell site-footer__bottom">
          <p>{t.footer.copyright}</p>
          <div className="site-footer__legal">
            <Link to="/terminos">{t.footer.terms}</Link>
            <Link to="/privacidad">{t.footer.privacy}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
